import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import useBuildUpdateWebhookRows from './buildUpdateWebhooksRows.helper';
import { DefaultValues, ToggleValues } from './types';
import { UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';

const { Box, Button, Title, Table, TextField, Toggle } = vp;

type UpdateWebhookProps = {
  defaultValues: DefaultValues;
  toggleValues: ToggleValues;
  setToggleValues: React.Dispatch<React.SetStateAction<ToggleValues>>;
  onSubmit: (values: DefaultValues) => void;
};
type FC = FunctionComponent<UpdateWebhookProps>;

const UpdateWebhook: FC = (props: UpdateWebhookProps) => {
  const { defaultValues, toggleValues, setToggleValues, onSubmit } = props;
  const updateWebhookRows = useBuildUpdateWebhookRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks.Update' });
  const { t: tWebhooks } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();

  return (
    <Formik
      enableReinitialize
      initialValues={defaultValues}
      onSubmit={(values) => onSubmit(values)}
      validateOnChange
      validateOnBlur
      validationSchema={createdValidationSchema()}
    >
      {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
        // A toggled-on field blocks Review if it's empty or fails URL validation; toggled-off fields are ignored.
        const isDisabled = updateWebhookRows.some(
          ({ id }) => toggleValues[id] && (!values[id as keyof DefaultValues] || !!errors[id]),
        );

        const updateWebhookTable: vp.TableProps = {
          name: 'update-webhook-fields',
          columns: {
            Field: { header: t('Form.event') },
            Toggle: { header: t('Form.enable'), align: 'left' },
            Input: { header: t('Form.url') },
          },
          rows: updateWebhookRows.map(({ label, id, preset }) => ({
            Field: label,
            Toggle: (
              <Box flx="row" jc="fs" ps="sm">
                <Toggle
                  name={id}
                  onBlur={handleBlur}
                  onChange={() => {
                    const isEnabling = !toggleValues[id];
                    setToggleValues((prev) => ({ ...prev, [id]: isEnabling }));
                    if (!isEnabling) setFieldValue(id, '');
                  }}
                  value={toggleValues[id]}
                />
              </Box>
            ),
            Input: (
              <TextField
                preset={preset}
                key={id}
                name={id}
                variant="standard"
                placeholder={t('Form.urlPlaceholder')}
                onBlur={handleBlur}
                disabled={!toggleValues[id]}
                onChange={handleChange}
                value={values[id as keyof DefaultValues]}
                error={errors[id] ? errors[id] : null}
              />
            ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Box flx="col" mb="md" gapSize="16px">
              <UtilButton
                iconLeft="chevron_left"
                size="large"
                onClick={() => {
                  if (history.length > 1) {
                    history.goBack();
                  } else {
                    history.replace('/settings/webhooks');
                  }
                }}
              >
                {history.length > 1 ? tGlobal('Buttons.back') : tWebhooks('pageTitle')}
              </UtilButton>
              <Title title={t('pageTitle')} />
            </Box>
            <Table {...updateWebhookTable} />
            <Box mt="md" jc="fe">
              <Button id="Webhook-review" type="submit" label={tGlobal('Buttons.review')} isDisabled={isDisabled} />
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    const urlValidation = Yup.string().url(t('Form.invalidURLValidation'));
    return Yup.object().shape(Object.fromEntries(updateWebhookRows.map(({ id }) => [id, urlValidation])));
  }
};

export default UpdateWebhook;
